import React from "react";
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  List,
  ListItem,
  Checkbox,
  IconButton,
  Button,
  Tooltip
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { v4 as uuidv4 } from "uuid";

import {
  DOMAIN_OPTIONS,
  READING_LEVEL_OPTIONS,
  TONE_OPTIONS,
  CITATION_STYLE_OPTIONS,
  MEDIA_DENSITY_OPTIONS,
  CODE_LANGUAGE_OPTIONS,
  LANGUAGE_OPTIONS
} from "./TemplateEnums";

/**
 * PromptBuilder supports editing fragment list and template metadata.
 * Provides safe defaults so templateMeta/setTemplateMeta won't be undefined.
 */
export default function PromptBuilder({
  fragments,
  setFragments,
  templateMeta = {},
  setTemplateMeta = () => {}
}) {
  // Destructure with defaults even if templateMeta is an empty object
  const {
    domain = "",
    sectionsPerLesson = 1,
    readingLevel = "",
    tone = "",
    mediaDensity = "",
    codeLanguage = "",
    language = "",
    timePerLesson = 0
  } = templateMeta;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Settings
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2, mb: 3 }}>
        <Tooltip title="Subject area for the course, e.g., STEM or Arts">
          <FormControl fullWidth>
            <InputLabel id="domain-label">Domain</InputLabel>
            <Select
              id="domain-select"
              labelId="domain-label"
              value={domain}
              label="Domain"
              onChange={(e) => {
                console.log("Domain →", e.target.value);
                setTemplateMeta(m => ({ ...m, domain: e.target.value }));
              }}
            >
              {DOMAIN_OPTIONS.map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Tooltip>

        <Tooltip title="Number of sections to include in each lesson">
          <TextField
            label="Sections per Lesson"
            type="number"
            fullWidth
            value={sectionsPerLesson}
            onChange={(e) => {
              console.log("sectionsPerLesson →", e.target.value);
              setTemplateMeta(m => ({ ...m, sectionsPerLesson: Number(e.target.value) }));
            }}
          />
        </Tooltip>

        <Tooltip title="Complexity level appropriate for the target audience">
          <Autocomplete
            options={READING_LEVEL_OPTIONS}
            value={readingLevel}
            onChange={(_, v) => setTemplateMeta(m => ({ ...m, readingLevel: v || "" }))}
            renderInput={(params) => <TextField {...params} label="Reading Level" fullWidth />}            
          />
        </Tooltip>

        <Tooltip title="Writing tone, e.g., professional or conversational">
          <Autocomplete
            options={TONE_OPTIONS}
            value={tone}
            onChange={(_, v) => setTemplateMeta(m => ({ ...m, tone: v || "" }))}
            renderInput={(params) => <TextField {...params} label="Tone" fullWidth />}            
          />
        </Tooltip>

        <Tooltip title="Amount of media (images, videos) per lesson">
          <FormControl fullWidth>
            <InputLabel id="media-density-label">Media Density</InputLabel>
            <Select
              id="media-density-select"
              labelId="media-density-label"
              value={mediaDensity}
              label="Media Density"
              onChange={(e) => setTemplateMeta(m => ({ ...m, mediaDensity: e.target.value }))}
            >
              {MEDIA_DENSITY_OPTIONS.map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Tooltip>

        <Tooltip title="Programming language for code examples">
          <Autocomplete
            options={CODE_LANGUAGE_OPTIONS}
            value={codeLanguage}
            onChange={(_, v) => setTemplateMeta(m => ({ ...m, codeLanguage: v || "" }))}
            renderInput={(params) => <TextField {...params} label="Code Language" fullWidth />}            
          />
        </Tooltip>

        <Tooltip title="Natural language of the lesson content">
          <FormControl fullWidth>
            <InputLabel id="language-label">Lesson Language</InputLabel>
            <Select
              id="language-select"
              labelId="language-label"
              value={language}
              label="Lesson Language"
              onChange={(e) => setTemplateMeta(m => ({ ...m, language: e.target.value }))}
            >
              {LANGUAGE_OPTIONS.map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Tooltip>
        <Tooltip title="Estimated time (in minutes) to complete each lesson">
          <TextField
            label="Time / Lesson (min)"
            type="number"
            fullWidth
            value={timePerLesson}
            onChange={(e) => setTemplateMeta(m => ({ ...m, timePerLesson: Number(e.target.value) }))}
          />
        </Tooltip>
      </Box>

      <Typography variant="subtitle1" gutterBottom>
        Lesson Components
      </Typography>
      <List dense disablePadding>
        {fragments.map((frag, idx) => (
          <ListItem
            key={frag.id}
            sx={{
              display: "flex",
              alignItems: frag.multiline ? "flex-start" : "center",
              gap: 1,
              px: 0,
              pr: 20
            }}
            secondaryAction={
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  aria-label="move-up"
                  disabled={idx === 0}
                  onClick={() =>
                    setFragments(fs => {
                      const i = fs.findIndex(f => f.id === frag.id);
                      if (i > 0) {
                        const c = [...fs];
                        [c[i - 1], c[i]] = [c[i], c[i - 1]];
                        return c;
                      }
                      return fs;
                    })
                  }
                >
                  <ArrowUpwardIcon />
                </IconButton>
                <IconButton
                  aria-label="move-down"
                  disabled={idx === fragments.length - 1}
                  onClick={() =>
                    setFragments(fs => {
                      const i = fs.findIndex(f => f.id === frag.id);
                      if (i < fs.length - 1) {
                        const c = [...fs];
                        [c[i + 1], c[i]] = [c[i], c[i + 1]];
                        return c;
                      }
                      return fs;
                    })
                  }
                >
                  <ArrowDownwardIcon />
                </IconButton>
                <IconButton
                  edge="end"
                  aria-label="delete"
                  onClick={() => setFragments(fs => fs.filter(f => f.id !== frag.id))}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            }
          >
            <Checkbox
              checked={frag.checked}
              onChange={e =>
                setFragments(fs =>
                  fs.map(f =>
                    f.id === frag.id ? { ...f, checked: e.target.checked } : f
                  )
                )
              }
            />
            <Box sx={{ width: 150, pr: 1 }}>
              <Typography variant="body2">
                {frag.label}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <TextField
                variant="standard"
                size="small"
                value={frag.text}
                onChange={e =>
                  setFragments(fs =>
                    fs.map(f =>
                      f.id === frag.id ? { ...f, text: e.target.value } : f
                    )
                  )
                }
                fullWidth
                multiline={!!frag.multiline}
                minRows={frag.multiline ? frag.rows || 3 : 1}
              />
            </Box>
            <Box sx={{ width: 100, ml: 1 }}>
              <FormControl variant="standard" fullWidth size="small">
                <InputLabel id={`scope-label-${frag.id}`}>Scope</InputLabel>
                <Select
                  labelId={`scope-label-${frag.id}`}
                  value={frag.scope}
                  label="Scope"
                  onChange={e =>
                    setFragments(fs =>
                      fs.map(f =>
                        f.id === frag.id ? { ...f, scope: e.target.value } : f
                      )
                    )
                  }
                >
                  <MenuItem value="lesson">Lesson</MenuItem>
                  <MenuItem value="section">Section</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </ListItem>
        ))}
      </List>
      <Box mt={2} display="flex" gap={1}>
        <Button
          startIcon={<AddIcon />}
          onClick={() =>
            setFragments(fs => [
              ...fs,
              {
                id: uuidv4(),
                label: "Custom",
                text: "",
                cssClass: "custom-card",
                multiline: false,
                rows: 1,
                checked: true,
                scope: "section"
              }
            ])
          }
        >
          Add custom component
        </Button>
      </Box>
    </Box>
  );
}
